/**
 * Web analytics for the Control Room — anonymous, opt-in, and gated on the
 * Settings "Share anonymous usage" toggle (default OFF). Uses PostHog (same
 * project as the desktop app). PostHog is only initialized once the user has
 * opted in, and we deliberately keep it anonymous:
 *   - autocapture OFF (no blanket DOM/click capture)
 *   - manual pageviews only
 *   - person_profiles "identified_only" + we never identify() with PII (email)
 * The preference mirrors the server setting in localStorage so boot is correct
 * before the /settings round-trip.
 */
import posthog from "posthog-js";

const PROJECT_KEY = "phc_vBFjiDWP5L798A2kFBR7We5WGCeHBTrhD3oN9XXkA8Z6";
const API_HOST = "https://us.i.posthog.com";
const PREF_KEY = "adaptr-telemetry"; // "1" = opted in, anything else = opted out

let initialized = false;

function startPostHog(): void {
  if (initialized) return;
  posthog.init(PROJECT_KEY, {
    api_host: API_HOST,
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: true,
    disable_session_recording: true,
    person_profiles: "identified_only",
  });
  initialized = true;
}

/** Opt in: init (if needed) + resume capturing. */
export function enableAnalytics(): void {
  localStorage.setItem(PREF_KEY, "1");
  if (!initialized) startPostHog();
  else posthog.opt_in_capturing();
}

/** Opt out: stop capturing (no further events leave the device). */
export function disableAnalytics(): void {
  localStorage.setItem(PREF_KEY, "0");
  if (initialized) posthog.opt_out_capturing();
}

/** Apply the saved preference at boot. Does nothing unless opted in. */
export function initAnalytics(): void {
  if (localStorage.getItem(PREF_KEY) === "1") startPostHog();
}

/** Reconcile with the server-stored setting (covers another device / first load). */
export function syncAnalyticsPref(enabled: boolean): void {
  if (enabled) enableAnalytics();
  else disableAnalytics();
}

export function capturePageview(path: string): void {
  if (!initialized) return;
  posthog.capture("$pageview", { $current_url: window.location.origin + path, path });
}

export function captureEvent(name: string, properties?: Record<string, unknown>): void {
  if (!initialized) return;
  posthog.capture(name, properties);
}
